// apps/patient/src/components/video/VideoRoom.tsx

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Video, VideoOff, Mic, MicOff, PhoneOff, 
  Monitor, MessageSquare, Settings, Loader,
  AlertCircle, WifiOff, Users
} from 'lucide-react';
import { patientApiService } from '../../services/api';
import { PageHeader } from '../layout/PageHeader';

// TypeScript declaration for JitsiMeetExternalAPI
declare global {
  interface Window {
    JitsiMeetExternalAPI: any;
  }
}

export function VideoRoom() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>(); // Get appointment ID from route
  const containerRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<any>(null);
  
  const [appointment, setAppointment] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [videoSession, setVideoSession] = useState<any>(null);
  const [jitsiReady, setJitsiReady] = useState(false); // New state to track Jitsi readiness
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [isInWaitingRoom, setIsInWaitingRoom] = useState(true);
  
  // Control states
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  
  // First, fetch the appointment
  useEffect(() => {
    const fetchAppointment = async () => {
      try {
        const appointmentData = await patientApiService.getAppointmentDetail(id!);
        setAppointment(appointmentData);
      } catch (err: any) {
        console.error('Error fetching appointment:', err);
        setError('Failed to load appointment details');
        setLoading(false);
      }
    };
    
    if (id) {
      fetchAppointment();
    } else {
      setError('No appointment ID provided');
      setLoading(false);
    }
  }, [id]);
  
  // Step 2: Get video session when appointment is loaded
  useEffect(() => {
    if (!appointment || !appointment.is_telehealth) {
      if (appointment && !appointment.is_telehealth) {
        setError('This appointment is not a telehealth visit');
        setLoading(false);
      }
      return;
    }
    
    const setupVideoSession = async () => {
      try {
        
        // Get video session from appointment
        const sessions = await patientApiService.getVideoSessionByAppointment(appointment.id);
        
        if (!sessions || sessions.length === 0) {
          throw new Error('Video session not yet started. Please wait for your provider to start the session.');
        }
        
        const session = Array.isArray(sessions) ? sessions[0] : sessions;
        
        // Check if provider has started the session
        if (session.status !== 'active' && !session.provider_joined_at) {
          throw new Error('Your provider has not started the video session yet. Please wait and try again in a moment.');
        }
        
        if (!session.patient_token) {
          throw new Error('Video session is missing authentication. Please contact support.');
        }
        
        setVideoSession(session);
        
        // Load Jitsi External API script
        if (!window.JitsiMeetExternalAPI) {
          const script = document.createElement('script');
          script.src = 'https://meet.prismehr.com/external_api.js';
          script.async = true;
          script.onload = () => {
            setJitsiReady(true);
            setLoading(false); // NOW we can show the UI
          };
          script.onerror = (error) => {
            console.error('Failed to load Jitsi script:', error);
            setError('Failed to load video conferencing service');
            setLoading(false);
          };
          document.body.appendChild(script);
        } else {
          setJitsiReady(true);
          setLoading(false); // NOW we can show the UI
        }
      } catch (err: any) {
        console.error('Error setting up video session:', err);
        setError(err.message || 'Failed to initialize video session');
        setLoading(false);
      }
    };
    
    setupVideoSession();
  }, [appointment]);
  
  // Step 3: Initialize Jitsi AFTER the container is rendered
  useEffect(() => {
    
    if (!jitsiReady || !videoSession || !containerRef.current || apiRef.current) {
      return;
    }
    
    initializeJitsi();
  }, [jitsiReady, videoSession, loading]); // Also depend on loading to ensure UI is rendered
  
  const initializeJitsi = () => {
    if (!containerRef.current) {
      console.error('[ERROR] Container ref not available when it should be!');
      return;
    }
    
    if (!videoSession) {
      console.error('[ERROR] Video session not available when it should be!');
      return;
    }

    
    const domain = 'meet.prismehr.com';
    const patientName = videoSession.patient_name || 'Patient';
    
    const options = {
      roomName: videoSession.room_name,
      jwt: videoSession.patient_token,
      width: '100%',
      height: '100%',
      parentNode: containerRef.current,
      configOverwrite: {
        startWithAudioMuted: false,
        startWithVideoMuted: false,
        disableModeratorIndicator: false,
        enableEmailInStats: false,
        enableWelcomePage: false,
        enableClosePage: false,
        prejoinPageEnabled: false, // Don't show pre-join for patients
        disableDeepLinking: true,
        liveStreamingEnabled: false,
        transcribingEnabled: false,
        recordingEnabled: false, // Patients can't record
        enableLobbyChat: false,
        requireDisplayName: false,
        enableInsecureRoomNameWarning: false,
        disableInviteFunctions: true,
        doNotStoreRoom: true,
        // Patient-specific settings
        toolbarButtons: [
          'microphone',
          'camera',
          'chat',
          'raisehand',
          'tileview',
          'videobackgroundblur',
          'fullscreen',
          'fodeviceselection',
          'hangup',
          'help'
        ]
      },
      interfaceConfigOverwrite: {
        TOOLBAR_BUTTONS: [
          'microphone',
          'camera',
          'chat',
          'raisehand',
          'tileview',
          'videobackgroundblur',
          'fullscreen',
          'fodeviceselection',
          'hangup',
          'help'
        ],
        SHOW_JITSI_WATERMARK: false,
        SHOW_WATERMARK_FOR_GUESTS: false,
        SHOW_BRAND_WATERMARK: false,
        BRAND_WATERMARK_LINK: '',
        SHOW_POWERED_BY: false,
        SHOW_PROMOTIONAL_CLOSE_PAGE: false,
        TOOLBAR_ALWAYS_VISIBLE: true,
        DISABLE_PRESENCE_STATUS: false,
        DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
        // Hide recording button for patients
        HIDE_RECORDING_LABEL: true,
        DISABLE_RECORDING_INDICATOR: false
      },
      userInfo: {
        displayName: patientName,
        email: videoSession.patient_email || ''
      }
    };
    
    try {
      apiRef.current = new window.JitsiMeetExternalAPI(domain, options);
      
      // Event listeners
      apiRef.current.addEventListener('videoConferenceJoined', handleConferenceJoined);
      apiRef.current.addEventListener('videoConferenceLeft', handleConferenceLeft);
      apiRef.current.addEventListener('audioMuteStatusChanged', handleAudioMuteStatusChanged);
      apiRef.current.addEventListener('videoMuteStatusChanged', handleVideoMuteStatusChanged);
      apiRef.current.addEventListener('readyToClose', handleReadyToClose);
      apiRef.current.addEventListener('knockingParticipant', handleKnocking);
      apiRef.current.addEventListener('participantJoined', handleParticipantJoined);
      
    } catch (err: any) {
      console.error('[ERROR] Failed to create JitsiMeetExternalAPI:', err);
      setError(`Failed to initialize video: ${err.message}`);
    }
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (apiRef.current) {
        cleanupSession();
      }
    };
  }, []);
  
  // Event handlers
  const handleConferenceJoined = async () => {
    setConnectionStatus('connected');
    setIsInWaitingRoom(false);
    
    // Mark patient as joined
    if (videoSession) {
      try {
        await patientApiService.joinVideoSession(videoSession.id);
      } catch (err) {
        console.error('Error marking patient as joined:', err);
      }
    }
  };
  
  const handleConferenceLeft = () => {
    setConnectionStatus('disconnected');
  };
  
  const handleAudioMuteStatusChanged = (status: any) => {
    setIsMuted(status.muted);
  };
  
  const handleVideoMuteStatusChanged = (status: any) => {
    setIsVideoOn(!status.muted);
  };
  
  const handleKnocking = () => {
    setIsInWaitingRoom(true);
  };
  
  const handleParticipantJoined = () => {
    setIsInWaitingRoom(false);
  };
  
  const handleReadyToClose = () => {
    cleanupSession();
  };
  
  // Control functions
  const toggleAudio = () => {
    apiRef.current?.executeCommand('toggleAudio');
  };
  
  const toggleVideo = () => {
    apiRef.current?.executeCommand('toggleVideo');
  };
  
  const endCall = async () => {
    if (window.confirm('Are you sure you want to leave this video visit?')) {
      await cleanupSession();
      navigate(`/appointments/${appointment.id}`);
    }
  };
  
  const cleanupSession = async () => {
    if (apiRef.current) {
      try {
        apiRef.current.executeCommand('hangup');
        apiRef.current.dispose();
      } catch (err) {
        console.error('Error disposing Jitsi:', err);
      }
      apiRef.current = null;
    }
    
    if (videoSession) {
      try {
        await patientApiService.leaveVideoSession(videoSession.id);
      } catch (err) {
        console.error('Error leaving video session:', err);
      }
    }
  };
  
  // Render states
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-white mx-auto mb-4" />
          <p className="text-white">Connecting to your appointment...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Unable to Join Video Visit</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          <button
            onClick={() => navigate(`/appointments/${id}`)}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Return to Appointment
          </button>
        </div>
      </div>
    );
  }
  
  // Main UI - This will render once loading is false and error is null
  return (
    <div className="h-screen flex flex-col bg-gray-900">
      {/* Header - Mobile Optimized */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h2 className="text-white font-semibold truncate">
              {appointment?.provider?.title} {appointment?.provider?.first_name} {appointment?.provider?.last_name}
            </h2>
            <div className="flex items-center space-x-2 mt-1">
              <span className={`
                px-2 py-0.5 rounded text-xs font-medium
                ${connectionStatus === 'connected' ? 'bg-green-600 text-white' : 
                  connectionStatus === 'connecting' ? 'bg-yellow-600 text-white' : 
                  'bg-red-600 text-white'}
              `}>
                {connectionStatus === 'connected' ? 'Connected' : 
                 connectionStatus === 'connecting' ? 'Connecting...' : 
                 'Disconnected'}
              </span>
              {isInWaitingRoom && (
                <span className="text-xs text-gray-300">
                  Waiting for provider to admit you
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Waiting Room Message */}
      {isInWaitingRoom && (
        <div className="bg-blue-600 px-4 py-3 flex items-center">
          <Users className="h-5 w-5 text-white mr-3 flex-shrink-0" />
          <p className="text-white text-sm">
            You're in the waiting room. Your provider will let you in shortly.
          </p>
        </div>
      )}
      
      {/* Video container - Critical: This must exist before Jitsi init */}
      <div className="flex-1 relative">
        <div ref={containerRef} className="h-full" id="jitsi-container" />
      </div>
      
      {/* Control bar - Mobile Optimized */}
      <div className="bg-gray-800 border-t border-gray-700 px-4 py-4 safe-area-inset-bottom">
        <div className="flex items-center justify-center space-x-3">
          <button
            onClick={toggleAudio}
            className={`p-3 rounded-full transition-colors ${
              isMuted ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700 hover:bg-gray-600'
            }`}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? (
              <MicOff className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            ) : (
              <Mic className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            )}
          </button>
          
          <button
            onClick={toggleVideo}
            className={`p-3 rounded-full transition-colors ${
              !isVideoOn ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700 hover:bg-gray-600'
            }`}
            aria-label={isVideoOn ? 'Turn off camera' : 'Turn on camera'}
          >
            {!isVideoOn ? (
              <VideoOff className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            ) : (
              <Video className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            )}
          </button>
          
          <button
            onClick={endCall}
            className="p-3 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
            aria-label="Leave call"
          >
            <PhoneOff className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}